import React from "react";
import { RankBadge, RankBadgeInsignia, type BadgeLook } from "../lobby/start/icons";
import { insigniaForValue, ladderSteps } from "../../shared/rating/tiers";

// /debug/rank-badges: the start page's rank badge at every step of the ladder
// (Bronze I to Wormlord), at three fills of its track, plus the placement and
// signed-out badges and the small size. Two designs: the one the game uses, and
// the other one tried, for comparison.
const NUMERAL_PIPS: Record<string, number> = { I: 1, II: 2, III: 3 };
const FILLS = [15, 50, 90];
type Badge = React.ComponentType<{ look: BadgeLook; className?: string; inner?: number }>;

function Ladder({ title, Badge }: { title: string; Badge: Badge }) {
  const steps = ladderSteps();
  const cell: React.CSSProperties = { display: "flex", flexDirection: "column", alignItems: "center", gap: 6, width: 96 };
  const note: React.CSSProperties = { color: "#8ea3bd", marginBottom: 10 };
  const lookOf = (v: number, pct: number): BadgeLook => {
    const ins = insigniaForValue(v);
    return { kind: "ranked", color: ins.tier.color, pct, pips: ins.numeral ? NUMERAL_PIPS[ins.numeral] : 0, value: v };
  };
  return (
    <section style={{ marginBottom: 44 }}>
      <h2 style={{ font: '700 15px "Chakra Petch", sans-serif', margin: "0 0 12px", color: "#19f0b0" }}>{title}</h2>
      {FILLS.map((fill) => (
        <div key={fill} style={{ marginBottom: 26 }}>
          <div style={note}>Track {fill}% of the way to the next step</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 14 }}>
            {steps.map((v) => {
              const ins = insigniaForValue(v);
              return (
                <div key={v} style={cell}>
                  <Badge look={lookOf(v, fill)} className="rb60" />
                  <span style={{ color: ins.tier.color, fontWeight: 700 }}>{ins.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      ))}
      <div style={note}>Large, to judge the track's start and its corners</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 24, marginBottom: 26 }}>
        {[1050, 1450, 1750, 1950, 2250].map((v) => <Badge key={v} look={lookOf(v, 88)} className="rb140" inner={80} />)}
      </div>
      <div style={note}>Placements (0 to 5 played) and signed out</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginBottom: 26 }}>
        {[0, 1, 2, 3, 4, 5].map((n) => (
          <div key={n} style={cell}><Badge look={{ kind: "placing", pct: (n / 5) * 100 }} className="rb60" /><span>{n} / 5</span></div>
        ))}
        <div style={cell}><Badge look={{ kind: "locked" }} className="rb60" /><span>Signed out</span></div>
      </div>
      <div style={note}>Small, as on the phone's Ranked button</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
        {steps.map((v) => <div key={v} style={{ width: 38 }}><Badge look={lookOf(v, 60)} className="rb38" inner={21} /></div>)}
      </div>
    </section>
  );
}

export default function RankBadgesSheet() {
  return (
    <div style={{ minHeight: "100vh", background: "#060b18", color: "#e9f3ff", font: '500 13px "Outfit", system-ui, sans-serif', padding: "28px 32px" }}>
      <style>{".rb60{width:60px;height:60px}.rb38{width:38px;height:38px}.rb140{width:140px;height:140px}"}</style>
      <h1 style={{ font: '700 20px "Chakra Petch", sans-serif', margin: "0 0 18px" }}>Rank badges</h1>
      <Ladder title="A. In the game: the filled badge. Chevrons to Gold, the ladder's icons from Platinum up" Badge={RankBadge} />
      <Ladder title="B. For comparison: the ladder's insignia on its own, in the same track" Badge={RankBadgeInsignia} />
    </div>
  );
}
